import { closeX } from './popup.js';
import { $, el } from '../state/store.js';
import { on } from '../state/bus.js';
import { currentUser, isAnonymous } from '../services/auth.js';
import { creditBalance, hasActivePack, hasEverPurchased, loadEntitlements, packUntil } from '../services/entitlements.js';
import { formatDate } from './calendar.js';
import { EXPORT_COST, hasLicence } from '../services/limits.js';
import { isConfigured } from '../services/supabase.js';
import { track } from '../services/analytics.js';
import { showSignIn } from './account.js';
import { showSettings } from './settings.js';
import { showTrial } from './trial.js';
import { toast } from './toast.js';

/* BILLING — one-time credit packs via Paddle (Paddle Billing, not Classic).

   Reuses the same overlay/card conventions as account.js
   (.authwrap/.authcard/.authrow/.authnote are generic despite the "auth-"
   naming) on its own #billingwrap element, so none of the three modals can
   collide.

   Paddle.js (loaded generically in index.html, harmless before a client
   token exists) gives an in-tab checkout overlay. Unlike LemonSqueezy,
   there is no plain-link/new-tab fallback worth porting if the script
   fails to load — Paddle's no-JS hosted checkout is restricted to approved
   mobile-app partners on live accounts, so it wouldn't actually work here;
   openCheckout() surfaces a toast instead. Either way, a purchase is
   confirmed by the PADDLE_WEBHOOK_SECRET-verified webhook on the server
   (see supabase/functions/paddle-webhook), never trusted client-side — the
   eventCallback below and the polling in pollForCredit are purely a "don't
   make them refresh the page" nicety, not a grant path. */

/* Must match the three packs landing/content/landing.{en,fi}.json advertise
   (pricing.packs) — those prices are what somebody read before clicking
   through, and a mismatch here would mean the checkout charges something
   other than what was promised. There is no shared source between the two:
   the landing JSON is translator-editable marketing copy, this is wiring to
   a specific Paddle Price object, and the two have different reasons to
   change on different days. Keep them in step by hand. */
const PACKS = [
  { credits: 20,  priceEnv: 'VITE_PADDLE_PRICE_ID_20',  label: '20 credits',  price: '49€'  },
  { credits: 60,  priceEnv: 'VITE_PADDLE_PRICE_ID_60',  label: '60 credits',  price: '119€' },
  { credits: 200, priceEnv: 'VITE_PADDLE_PRICE_ID_200', label: '200 credits', price: '349€' },
];

const CLIENT_TOKEN = import.meta.env.VITE_PADDLE_CLIENT_TOKEN;
const ENVIRONMENT = import.meta.env.VITE_PADDLE_ENVIRONMENT || 'sandbox';
const isPackConfigured = p => !!(CLIENT_TOKEN && import.meta.env[p.priceEnv]);
const PADDLE_CONFIGURED = PACKS.every(isPackConfigured);

/* Paddle.Initialize() throws if called twice, and window.Paddle only
   exists once the deferred script from index.html has actually run — so
   this is a lazy, call-time check/init (mirroring the old
   `window.LemonSqueezy?.Url?.Open` pattern) rather than something run
   eagerly from initBilling(), which can't assume load order against a
   plain <script defer>. eventCallback only ever triggers a re-read of the
   server-owned balance — see the file header on why that's safe. */
let paddleReady = false;
function onPaddleEvent(e) {
  if (e?.name === 'checkout.completed') loadEntitlements();
}
function ensurePaddleInit() {
  if (paddleReady) return true;
  if (!window.Paddle) return false;
  if (ENVIRONMENT !== 'production') window.Paddle.Environment.set(ENVIRONMENT);
  window.Paddle.Initialize({ token: CLIENT_TOKEN, eventCallback: onPaddleEvent });
  paddleReady = true;
  return true;
}

let overlay = null;
function ensureOverlay() {
  if (overlay) return overlay;
  overlay = el('div', 'authwrap hidden');
  overlay.id = 'billingwrap';
  document.body.appendChild(overlay);
  return overlay;
}
const hide = () => ensureOverlay().classList.add('hidden');
/* Billing is reachable as a drill-down FROM other modals (a locked template
   row, a locked template) as well as directly from the credits chip. Every
   such overlay shares the same .authwrap class and z-index — stacking order
   is otherwise just DOM append order, which is exactly what left an earlier
   version of this reachable-but-covered by whichever modal opened it,
   depending on which had been created first. Only one modal should ever be
   the one accepting clicks, so opening this one hides any other. */
const show = () => {
  document.querySelectorAll('.authwrap').forEach(w => { if (w !== overlay) w.classList.add('hidden'); });
  ensureOverlay().classList.remove('hidden');
};

/**
 * After opening checkout, poll for the webhook having landed rather than
 * make the buyer refresh the page manually — the webhook is typically
 * near-instant but isn't synchronous with the checkout closing.
 */
function pollForCredit(before, pack) {
  let tries = 0;
  const iv = setInterval(async () => {
    tries++;
    const { credit_balance } = await loadEntitlements();
    if (credit_balance > before) {
      clearInterval(iv);
      track('credits_purchased', { credits: pack.credits, balance_after: credit_balance });
    } else if (tries >= 10) clearInterval(iv);
  }, 1500);
}

/* A REAL ACCOUNT: signed in with an email, not the anonymous session every
   visitor is given on arrival. Credits are tied to an account. An anonymous
   session lives in one browser's storage and is gone when that is cleared, so a
   pack bought on it could simply vanish along with what was paid for — and
   there would be no address to send a receipt to or to find the buyer by. So
   nothing here sells to one.

   Enforced where the money is taken, not only where it is asked for: the pack
   buttons are not drawn for an anonymous visitor, and openCheckout() checks
   again, because the session can change between the dialog being drawn and a
   button being pressed (signing out in another tab). It cannot be enforced on
   the server after the fact — by the time the webhook runs, the payment has
   already been taken, and refusing the credit then would keep the money and
   withhold the goods. */
const hasAccount = () => { const u = currentUser(); return !!u && !isAnonymous(); };

/* Somebody was sent to sign in FROM here, so the dialog is put back in front of
   them once they are in. Two ways back: the same page, when they signed in with
   a password (auth:changed fires with a real user), and a fresh load, when they
   used the emailed link — possibly in another browser, so it travels in the
   URL as ?next=credits and not in this module's memory. */
let resumeBilling = false;
const wantsBillingInUrl = () => new URLSearchParams(location.search).get('next') === 'credits';
function stripBillingIntent() {
  try {
    const u = new URL(location.href);
    u.searchParams.delete('next');
    history.replaceState(null, '', u.pathname + u.search + u.hash);
  } catch (_) { /* the address bar is a courtesy */ }
}
function maybeResumeBilling() {
  if (!hasAccount()) return;      // an anonymous session comes first; wait for the real one
  const fromUrl = wantsBillingInUrl();
  if (fromUrl) stripBillingIntent();
  if (resumeBilling || fromUrl) { resumeBilling = false; showBilling(); }
}

function openCheckout(pack) {
  const user = currentUser();
  if (!user || isAnonymous()) { showBilling(); return; }   // redrawn, it now asks them to sign in
  if (!ensurePaddleInit()) {
    toast('Checkout couldn’t load — check your connection or ad blocker and try again.', true);
    return;
  }
  const priceId = import.meta.env[pack.priceEnv];
  const before = creditBalance();
  window.Paddle.Checkout.open({
    items: [{ priceId, quantity: 1 }],
    customData: { owner_id: user.id },
    /* The account's own address, so the receipt goes where they can be found. */
    ...(user.email ? { customer: { email: user.email } } : {}),
  });
  pollForCredit(before, pack);
}

export function showBilling() {
  const w = ensureOverlay();
  w.innerHTML = '';
  const card = el('div', 'authcard');
  card.appendChild(el('h2', null, 'Buy credits'));
  closeX(card, hide);

  if (!PADDLE_CONFIGURED) {
    /* A WAY OUT, not just an apology.
       Without this the card said "Payments aren't set up yet." and offered
       only Close — telling somebody to buy a thing they cannot buy and
       leaving them there. Every other locked surface in the app routes
       somewhere: the Motion tab, the timeline dock and the video cards all
       offer the trial, and Settings' licence note offers this same door. A
       dead end here was the only one left.

       The licence door specifically (not the Motion one): whoever reached
       this card wanted the template library or a watermark-free export, which
       is a commercial-use question, and that is what 'licence' opens the form
       talking about. It files into the same Admin queue, marked so the reply
       can be the right one. */
    card.appendChild(el('p', 'authnote',
      'Payments aren’t set up yet — credits can’t be bought online for now. '
      + 'Commercial use is arranged directly in the meantime.'));
    const ask = el('button', 'licencelink', 'Ask about a licence');
    ask.type = 'button';
    ask.onclick = () => { hide(); showTrial({ from: 'licence' }); };
    const askWrap = el('p', 'authnote');
    askWrap.appendChild(ask);
    card.appendChild(askWrap);
  } else if (!hasAccount()) {
    /* Nothing to buy until there is somebody to buy it for. Two doors, because
       both people arrive here: the newcomer, who has no account, and the
       returning customer on a new browser, who has one and is anonymous only
       because this browser does not know it yet. */
    card.appendChild(el('p', 'authnote',
      'Credits belong to an account, so you need to be signed in to buy them. '
      + 'It takes an email address — no password needed. Anything you’ve made on this browser stays yours.'));
    const go = (mode) => () => { resumeBilling = true; hide(); showSignIn(mode, { next: 'credits' }); };
    const doors = el('div', 'authrow');
    const create = el('button', 'btn btn--pri', 'Create an account');
    create.type = 'button'; create.onclick = go('create');
    const signin = el('button', 'btn btn--gh', 'Sign in');
    signin.type = 'button'; signin.onclick = go('in');
    doors.append(create, signin);
    card.appendChild(doors);
  } else {
    /* Only here, where there is a pack to choose. It used to sit above both
       branches, so somebody who could not buy anything was first told what a
       credit is worth — and it said "each credit is one export", which stopped
       being true when a still became free and a video became ten. */
    card.appendChild(el('p', 'authnote',
      'The app is fully functional, and stills are free for non-commercial use. Any pack '
      + 'unlocks unlimited watermarked video, cloud projects and commercial use for 12 months '
      + 'from purchase — spending the credits doesn’t shorten that, and buying again restarts '
      + 'it. Credits take the watermark off: 10 for a video, 20 for a multi-screen one, 5 for '
      + 'an on-site preview.'));
    const list = el('div', 'projlist');
    for (const pack of PACKS) {
      const row = el('div', 'projitem');
      row.appendChild(el('span', 'projname', `${pack.label} — ${pack.price}`));
      const buy = el('button', 'btn btn--sm btn--pri', 'Buy');
      buy.onclick = () => openCheckout(pack);
      row.appendChild(buy);
      list.appendChild(row);
    }
    card.appendChild(list);
    // Said once, plainly, rather than folded into the paragraph above (which
    // is already carrying the prices and what a pack unlocks) or left for
    // somebody to discover a year later reading their own history.
    card.appendChild(el('p', 'note', 'Credits expire a year after they’re added.'));
  }

  const row = el('div', 'authrow');
  const close = el('button', 'btn btn--gh', 'Close');
  close.onclick = hide;
  row.appendChild(close);
  card.appendChild(row);

  w.appendChild(card);
  show();
}

function paintCreditsChip() {
  const chip = $('#creditsChip');
  if (!chip) return;
  /* A licence covers everything credits would buy, so there is nothing here to
     buy or to count — showing "Buy credits" to somebody who has an Advanced
     licence is an advert for something they already have. It returns if the
     licence lapses: this repaints on 'motion:changed'. */
  chip.classList.toggle('hidden', hasLicence());
  if (hasLicence()) return;
  const n = creditBalance();
  const plural = n === 1 ? '' : 's';
  chip.textContent = n > 0 ? (n + ' credit' + plural) : 'Buy credits';
  /* Not "n exports" any more: exports no longer cost one each. Stills are
     free, a video is 10 and a panelled one 20, so the honest tooltip names
     the prices rather than implying a count. "Always free" undersold it and
     read oddly next to the pricing table's own "commercial use rights" row:
     a still costs nothing whether or not anyone has bought a pack, but
     commercial rights come with one — for the 12 months it runs (0030). So
     the three cases are a live pack (whatever the balance, even 0), one
     whose 12 months have ended, and none ever. */
  const prices = `A clean video costs ${EXPORT_COST.video}, a multi-screen one `
    + `${EXPORT_COST.videoPanels}, an on-site preview ${EXPORT_COST.onsiteVideo}.`;
  const until = packUntil();
  chip.title = hasActivePack()
    ? `${n} credit${plural}. ${prices} Your pack runs until ${formatDate(until)} — video export, `
      + 'cloud projects and commercial use for stills until then.'
    : hasEverPurchased()
      ? `Your pack’s 12 months ended on ${until ? formatDate(until) : 'its anniversary'}. A new pack `
        + 'unlocks video export, cloud projects and commercial use for another 12 months.'
      : 'Any credit pack unlocks video export, cloud projects and commercial use for stills for 12 '
        + 'months. Stills stay free for non-commercial use either way.';
}

function mountToolbar() {
  // Sits next to the account chip account.js already mounts after
  // #solveChip — inserted after whichever of those two exists, so mount
  // order between the two modules doesn't matter.
  const after = $('#acctChip') || $('#solveChip');
  if (!after || $('#creditsChip')) return;
  const chip = el('button', 'btn btn--sm btn--gh', 'Buy credits');
  chip.id = 'creditsChip';
  /* Settings, not straight to Buy credits — the Credits section there already
     shows the balance, the ledger (listCreditHistory) and a Buy credits link
     of its own, so the one click a person makes on "N credits" answers "what
     have I got and where did it go" as well as "how do I get more". Every
     OTHER "Buy credits" in the app (the Projects lock, the cannotPay toast,
     the licence-lapsed note) still opens the dialog directly — those are
     already the buying action, not a status chip asking to be read first. */
  chip.onclick = showSettings;
  after.after(chip);
}

export function initBilling() {
  if (!isConfigured) return;      // local-only build: credits need a database, same as cloud projects
  /* The chip is back on the topbar. It was taken off while credits were
     unreachable — nothing could grant them and nothing could sell them, so a
     permanent "Buy credits" button asked every visitor to look at a door that
     did not open. Both halves exist now: the owner can put credits in an
     account from Admin, and an export spends them. Somebody holding a balance
     has to be able to SEE it, and "the user cannot see their credits" was the
     exact complaint. */
  mountToolbar();
  paintCreditsChip();
  on('entitlements:changed', paintCreditsChip);
  on('motion:changed', paintCreditsChip);
  on('auth:changed', maybeResumeBilling);
}
